import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Loader } from "@/components/Loader";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useGetStories } from "@/features/request/feedback-tool/feedbackTool/hooks/useGetStories.tsx";
import {
  Form,
  Points,
} from "@/features/request/feedback-tool/feedbackTool/pdf/components";
import { coordinateConvert } from "@/features/request/feedback-tool/feedbackTool/utils/coodinateConvert.ts";
import {
  CONTROLS_DEFAULT,
  CONTROLS_MIN,
  CONTROLS_STEP,
  ZOOM_STEP,
  zoomAroundPoint,
} from "@/features/request/feedback-tool/feedbackTool/utils/zoom.ts";

import usePdfToImages from "../hooks/usePdfToImages";
import { usePdfPinchZoom } from "./hooks/usePdfPinchZoom";

const DRAG_CLICK_THRESHOLD = 5;

export default function PFD({ url }: { url: string | undefined }) {
  const [page, setPage] = useState(1);
  const {
    applyZoom,
    controls,
    divImgRef,
    feedback,
    img,
    isTablet,
    pdfContainerRef,
    pdfScrollerRef,
    selected,
    setFeedBack,
    setFormFeedBack,
    setSelected,
    setSize,
    size,
    zoom,
  } = useFeedbackTool();
  const { imageUrls } = usePdfToImages({ attachmentId: img.id, pdfUrl: url });
  const imgRefs = useRef<Map<string, HTMLImageElement>>(new Map());
  const { data } = useGetStories({ parentId: img.id });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({
    scrollLeft: 0,
    scrollTop: 0,
    x: 0,
    y: 0,
  });
  const hasDraggedRef = useRef(false);

  usePdfPinchZoom({
    contentRef: pdfContainerRef,
    enabled: isTablet,
    hScrollerRef: pdfScrollerRef,
    onZoomEnd: (width) => applyZoom(width, controls),
    vScrollerRef: divImgRef,
  });

  const controlsRef = useRef(controls);

  controlsRef.current = controls;

  const isAtDefaultZoom = controls === CONTROLS_DEFAULT;

  // Measures divImgRef (unaffected by content size) rather than pdfScrollerRef
  // itself, since pdfScrollerRef's own width is set here — measuring it would
  // be circular.
  useLayoutEffect(() => {
    const content = pdfContainerRef.current;
    const scroller = pdfScrollerRef.current;
    const viewport = divImgRef.current;

    if (imageUrls.length === 0 || !content || !scroller || !viewport) return;

    const syncSize = () => {
      const marginLeft =
        Number.parseFloat(getComputedStyle(scroller).marginLeft) || 0;
      const availableWidth = viewport.clientWidth - marginLeft;

      if (availableWidth <= 0) return;

      const currentScrollerWidth = Number.parseFloat(scroller.style.width) || 0;

      if (Math.abs(availableWidth - currentScrollerWidth) > 1) {
        scroller.style.width = `${availableWidth}px`;
      }

      if (controlsRef.current !== CONTROLS_DEFAULT) return;

      const currentContentWidth = Number.parseFloat(content.style.width) || 0;

      if (Math.abs(availableWidth - currentContentWidth) > 1) {
        applyZoom(availableWidth, CONTROLS_DEFAULT);
      }
    };

    syncSize();

    const observer = new ResizeObserver(syncSize);

    observer.observe(viewport);

    return () => observer.disconnect();
  }, [imageUrls.length, isAtDefaultZoom]);

  const handleWheel = (event: React.WheelEvent) => {
    if (!event.ctrlKey) return;

    event.preventDefault();
    event.stopPropagation();

    const content = pdfContainerRef.current;
    const hScroller = pdfScrollerRef.current;
    const vScroller = divImgRef.current;

    if (!content || !hScroller || !vScroller) return;

    const zoomingIn = event.deltaY < 0;

    if (zoomingIn && controls <= CONTROLS_MIN) return;
    if (!zoomingIn && controls >= CONTROLS_DEFAULT) return;

    const rect = content.getBoundingClientRect();
    const normX = (event.clientX - rect.left) / rect.width;
    const normY = (event.clientY - rect.top) / rect.height;
    const newZoom = zoom + (zoomingIn ? ZOOM_STEP : -ZOOM_STEP);
    const newControls = controls + (zoomingIn ? -CONTROLS_STEP : CONTROLS_STEP);

    zoomAroundPoint(
      content,
      hScroller,
      vScroller,
      normX,
      normY,
      newZoom,
      newControls,
      applyZoom,
    );
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    const hContainer = pdfScrollerRef.current;
    const vContainer = divImgRef.current;

    if (!hContainer || !vContainer) return;

    event.preventDefault();
    setIsDragging(true);
    hasDraggedRef.current = false;
    setStartPos({
      scrollLeft: hContainer.scrollLeft,
      scrollTop: vContainer.scrollTop,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isDragging) return;
    const hContainer = pdfScrollerRef.current;
    const vContainer = divImgRef.current;

    if (!hContainer || !vContainer) return;

    const dx = event.clientX - startPos.x;
    const dy = event.clientY - startPos.y;

    if (Math.hypot(dx, dy) > DRAG_CLICK_THRESHOLD) hasDraggedRef.current = true;

    hContainer.scrollLeft = startPos.scrollLeft - dx;
    vContainer.scrollTop = startPos.scrollTop - dy;
  };

  useEffect(() => {
    const container = divImgRef.current;

    if (!container) return;

    const handleMouseUp = () => setIsDragging(false);

    container.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("mouseleave", handleMouseUp);

    return () => {
      container.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("mouseleave", handleMouseUp);
    };
  }, [divImgRef.current]);

  useEffect(() => {
    if (!data) {
      setFeedBack(null);

      return;
    }

    setFeedBack(() =>
      data.length > 0
        ? data?.map((feedback, index) => ({
            attachments: feedback?.attachments
              ? feedback?.attachments.map((att) => ({
                  attachmentId: Number(att.attachmentId),
                  name: att.name,
                  storyId: Number(att.storyId),
                  url: att.url,
                }))
              : [],
            commentBatchId: feedback.commentBatchId,
            description: feedback.comment ?? "",
            format: "PDF",
            id: index + 1,
            imgId: img.id,
            isResolved: feedback.isResolved ?? false,
            parentId: feedback.parentId,
            storyId: feedback.id,
            updatedAt: feedback.updatedAt,
            user: {
              id: feedback.user?.id,
              name: feedback.user?.name,
            },
            x: coordinateConvert(feedback.coordinates, "y"),
            y: coordinateConvert(feedback.coordinates, "x"),
          }))
        : null,
    );
  }, [data]);

  useEffect(() => {
    if (imageUrls.length > 0) {
      setSize("FULL");
    }
  }, [imageUrls]);

  const setImgsRef = (id: string, element: HTMLImageElement | null) => {
    if (element) {
      imgRefs.current.set(id, element);
    } else {
      imgRefs.current.delete(id);
    }
  };

  useEffect(() => {
    if (page) {
      const selectedRef = imgRefs.current?.get((page - 1).toString());

      if (selectedRef) {
        setTimeout(() => {
          selectedRef.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      }
    }
  }, [page]);

  useEffect(() => {
    const container = divImgRef.current;
    const pdfContainer = pdfContainerRef.current;

    if (!container || !selected || !pdfContainer) return;

    const positionY = Number(selected.y) * container.scrollHeight;

    const entries = [...imgRefs.current.entries()].map(([key, img]) => {
      const top = img.offsetTop;
      const height = img.offsetHeight;

      return {
        bottom: top + height,
        page: Number.parseInt(key),
        top,
      };
    });

    const pageMatch = entries.find(
      (entry) => positionY >= entry.top && positionY <= entry.bottom,
    );

    if (pageMatch) {
      setPage(pageMatch.page + 1);
    }
  }, [selected]);

  return imageUrls.length === 0 ? (
    <Loader className="bg-primary" />
  ) : (
    <div className="h-screen relative">
      <Form />
      <div className="flex flex-row">
        {size === "FULL" ? (
          <div className="hidden lg:flex w-50 h-screen flex-col items-center overflow-auto fixed top-14 left-0">
            {imageUrls.map((url, index) => (
              <div
                className={`${page === index + 1 ? "bg-gray-400" : ""} cursor-pointer`}
                key={url + "-" + index}
                onClick={() => setPage(index + 1)}
              >
                <img
                  alt={`Page ${index + 1}`}
                  className="m-2 w-30"
                  key={index}
                  src={url}
                />
              </div>
            ))}
          </div>
        ) : null}
        <div
          className="overflow-x-auto overflow-y-hidden lg:ml-50 shrink-0"
          ref={pdfScrollerRef}
        >
          <div
            className={`pt-1 relative min-w-full ${isDragging ? "cursor-grabbing" : "cursor-crosshair"}`}
            onClick={(event) => {
              const container = pdfContainerRef.current;

              if (!container || hasDraggedRef.current) return;

              const rect = pdfContainerRef.current.getBoundingClientRect();

              if (selected) return setSelected(null);

              setFormFeedBack({
                description: "",
                format: "PDF",
                id: feedback ? feedback.length + 1 : 1,
                imgId: 1,
                storyId: 0,
                type: "ADD",
                user: {
                  id: 0,
                  name: "",
                },
                x: (event.clientX - rect.left) / rect.width,
                y: (event.clientY - rect.top - 10) / rect.height,
              });
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onWheel={isTablet ? undefined : handleWheel}
            ref={pdfContainerRef}
            style={{ width: zoom }}
          >
            <Points />
            {imageUrls.map((url, index) => (
              <img
                alt={`Page ${index + 1}`}
                className="pl-2 pr-2 pt-1 pb-1 h-auto max-w-full w-full"
                key={index}
                ref={(el) => setImgsRef(index.toString(), el)}
                src={url}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
